import { faSquare, faSquareCheck } from '@fortawesome/free-regular-svg-icons';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { ReactElement, SetStateAction, useEffect, useState } from 'react';

import { TableFunctions, TableRenderFunctions, TableRowData } from '../../../types/common.types';

import { allRowsSelected, isPlaceholderObject } from '../../../utils/table.util';
import { noOp, removeCamelCase } from '../../../utils/util';

import Pagination from '../pagination/Pagination';
import TableFilter from './table-filter/TableFilter';

import { ReactComponent as DeleteIcon } from '../../../resources/icons/close.svg';

import './table.scss';

type Props = {
  data: TableRowData[];
  setData?: React.Dispatch<SetStateAction<TableRowData[]>>;
  dataTemplate: TableRowData;
  rowsPerPage: number;
  hiddenColumns?: string[];
  filter?: boolean;
  handleRowClick?: (object: TableRowData) => void;
  selectable?: boolean;
  columnFunctions?: TableFunctions;
  topActions?: ReactElement;
  bottomActions?: ReactElement;
  columnRenderFunctions?: TableRenderFunctions;
  onDeleteRowClick?: (object: TableRowData) => void;
};

const Table = ({
  data,
  setData = noOp,
  dataTemplate,
  rowsPerPage,
  hiddenColumns = [],
  filter = false,
  handleRowClick,
  selectable = false,
  columnFunctions = {},
  topActions,
  bottomActions,
  columnRenderFunctions = {},
  onDeleteRowClick
}: Props) => {
  const [pages, setPages] = useState<TableRowData[][]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [filteredData, setFilteredData] = useState(data);

  useEffect(() => {
    if (data.length === 0) {
      setFilteredData(data);
    }
  }, [data]);

  useEffect(() => {
    const copyOfFilteredData = structuredClone(filteredData);
    const pages: TableRowData[][] = [];

    if (copyOfFilteredData.length > 0) {
      while (copyOfFilteredData.length) {
        const pageData = copyOfFilteredData.splice(0, rowsPerPage);

        while (pageData.length < rowsPerPage) {
          pageData.push(dataTemplate);
        }

        pages.push(pageData);
      }
    } else {
      const page: TableRowData[] = [];
      for (let i = 0; i < rowsPerPage; i++) {
        page.push(dataTemplate);
      }
      pages.push(page);
    }

    setPages(pages);

    return () => setCurrentPage(0);
  }, [filteredData, rowsPerPage, dataTemplate]);

  const handleCellClick = (header: string, object: TableRowData) => {
    const cellFunction = columnFunctions[header];
    if (cellFunction) {
      cellFunction(object);
    } else if (handleRowClick && !isPlaceholderObject(object)) {
      handleRowClick(object);
    }
  };

  const handleSelectAllClick = () => {
    const copyOfData = structuredClone(data);
    const selected = allRowsSelected(copyOfData);
    copyOfData.forEach((object) => {
      object.selected = !selected;
    });
    setData(copyOfData);
  };

  const handleSelectClick = (selectedObject: TableRowData) => {
    const copyOfData = structuredClone(data);
    copyOfData.forEach((object) => {
      if (object.externalReference === selectedObject.externalReference) {
        object.selected = !object.selected;
      }
    });
    setData(copyOfData);
  };

  const renderTableHeader = () => {
    const object: TableRowData = pages.at(0)?.at(0) ?? {};
    const keys = Object.keys(object);

    const headers: ReactElement[] = keys
      .filter((key) => !hiddenColumns.includes(key))
      .map((key, index) => {
        return (
          <th key={index} className="table__header__row__cell">
            <span>{key.includes('#') ? key : removeCamelCase(key)}</span>
          </th>
        );
      });

    if (selectable) {
      headers.unshift(
        <th
          key="select-all-checkbox"
          className="table__header__row__checkbox"
          onClick={handleSelectAllClick}
          data-testid="select-all-checkbox"
        >
          <FontAwesomeIcon icon={allRowsSelected(data) ? faSquareCheck : faSquare} />
        </th>
      );
    }

    if (onDeleteRowClick) {
      headers.push(<th key="delete-column" className="table__header__row__delete" />);
    }

    return (
      <thead className="table__header">
        <tr className="table__header__row">{headers}</tr>
      </thead>
    );
  };

  const renderTableBody = () => {
    const rows: ReactElement[] = (pages[currentPage] ?? []).map((rowData, index) => {
      const row: ReactElement[] = [];
      for (const property in rowData) {
        if (!hiddenColumns.includes(property)) {
          const value = rowData[property];
          const columnFunction: ((object: TableRowData) => void) | undefined = columnFunctions[property];
          const renderFunction: ((object: TableRowData) => ReactElement) | undefined = columnRenderFunctions[property];

          if (renderFunction) {
            row.push(React.cloneElement(renderFunction(rowData), { key: property }));
          } else if (value === true && !columnFunction) {
            row.push(
              <td
                className="table__body__row__check"
                key={property}
                onClick={() => handleCellClick(property, rowData)}
                data-testid={`check-${property}-${index}`}
              >
                <FontAwesomeIcon icon={faCheck} />
              </td>
            );
          } else if (value === true && columnFunction) {
            row.push(
              <td
                className="table__body__row__checkbox"
                key={property}
                onClick={() => handleCellClick(property, rowData)}
                data-testid={`square-check-${property}-${index}`}
              >
                <FontAwesomeIcon icon={faSquareCheck} />
              </td>
            );
          } else if (value === false && columnFunction) {
            row.push(
              <td
                className="table__body__row__checkbox"
                key={property}
                onClick={() => handleCellClick(property, rowData)}
                data-testid={`square-${property}-${index}`}
              >
                <FontAwesomeIcon icon={faSquare} />
              </td>
            );
          } else {
            row.push(
              <td key={property} className="table__body__row__cell" onClick={() => handleCellClick(property, rowData)}>
                {value}
              </td>
            );
          }
        }
      }

      if (selectable) {
        row.unshift(
          <td key={`select-${index}`} className="table__body__row__checkbox">
            {rowData.selected && (
              <FontAwesomeIcon
                icon={faSquareCheck}
                onClick={() => handleSelectClick(rowData)}
                data-testid="select-row-checkbox"
              />
            )}
            {!rowData.selected && (
              <FontAwesomeIcon
                icon={faSquare}
                onClick={() => handleSelectClick(rowData)}
                data-testid="select-row-checkbox"
              />
            )}
          </td>
        );
      }

      const isPlaceholder = isPlaceholderObject(rowData);

      if (onDeleteRowClick) {
        row.push(
          <td key={`delete-${index}`} className="table__body__row__delete">
            {!isPlaceholder && (
              <DeleteIcon
                className="table__delete-icon"
                onClick={() => onDeleteRowClick(rowData)}
                data-testid="table-delete-icon"
              />
            )}
          </td>
        );
      }

      return (
        <tr
          className="table__body__row"
          key={index}
          style={{ cursor: handleRowClick && !isPlaceholder ? 'pointer' : 'initial' }}
          data-testid={`table-row-${index}`}
        >
          {row}
        </tr>
      );
    });

    return <tbody className="table__body">{rows}</tbody>;
  };

  return (
    <div className="table-container" data-testid="table-container">
      <div className="table__top-actions">
        {filter && <TableFilter data={data} setFilteredData={setFilteredData} setCurrentPage={setCurrentPage} />}
        {topActions}
      </div>
      {pages.length > 0 && (
        <table className="table">
          {renderTableHeader()}
          {renderTableBody()}
        </table>
      )}
      <div className="table__bottom-actions">
        <Pagination pageCount={pages.length} currentPage={currentPage} setCurrentPage={setCurrentPage} />
        {bottomActions}
      </div>
    </div>
  );
};

export default Table;
